import { supabase } from '../lib/supabaseClient';
import type { PlanType } from '../types/pricing';

export interface AIUsageData {
  questions_used_this_month: number;
  total_questions_all_time: number;
  usage_reset_date: string;
  limit: number;
  percentage_used: number;
}

/**
 * Get AI usage limits based on subscription plan
 */
export function getAIQuestionLimit(plan?: PlanType): number {
  if (!plan) return 20; // Default for users without subscription
  
  switch (plan) {
    case 'standard':
      return 20;
    case 'pro':
      return 200;
    case 'team':
    case 'business':
      return 200;
    default:
      return 20;
  }
}

/**
 * Get or create AI usage tracking for current user
 */
export async function getAIUsage(): Promise<AIUsageData | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Get user's subscription plan
    const subscriptionPlan = user.user_metadata?.subscription_plan as PlanType | undefined;
    const limit = getAIQuestionLimit(subscriptionPlan);

    // Get or create usage record
    let { data: usage, error } = await supabase
      .from('ai_usage_tracking')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    // Create if doesn't exist
    if (!usage && !error) {
      const { data: newUsage, error: insertError } = await supabase
        .from('ai_usage_tracking')
        .insert({
          user_id: user.id,
          questions_used_this_month: 0,
          total_questions_all_time: 0,
          usage_reset_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString()
        })
        .select()
        .maybeSingle();

      // Ignore duplicate key errors (user already has record)
      if (insertError && insertError.code !== '23505') {
        throw insertError;
      }
      usage = newUsage || usage;
    } else if (error) {
      throw error;
    }

    if (!usage) return null;

    return {
      questions_used_this_month: usage.questions_used_this_month,
      total_questions_all_time: usage.total_questions_all_time,
      usage_reset_date: usage.usage_reset_date,
      limit,
      percentage_used: Math.min((usage.questions_used_this_month / limit) * 100, 100)
    };
  } catch (error) {
    console.error('Error getting AI usage:', error);
    return null;
  }
}

/**
 * Increment AI usage counter
 */
export async function incrementAIUsage(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Get current usage
    const { data: usage } = await supabase
      .from('ai_usage_tracking')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!usage) {
      // Create new record with 1 usage (ignore if already exists)
      const { error: insertError } = await supabase
        .from('ai_usage_tracking')
        .insert({
          user_id: user.id,
          questions_used_this_month: 1,
          total_questions_all_time: 1
        });
      
      // Ignore duplicate key errors
      if (insertError && insertError.code !== '23505') {
        throw insertError;
      }
    } else {
      // Increment counters
      await supabase
        .from('ai_usage_tracking')
        .update({
          questions_used_this_month: usage.questions_used_this_month + 1,
          total_questions_all_time: usage.total_questions_all_time + 1
        })
        .eq('user_id', user.id);
    }

    return true;
  } catch (error) {
    console.error('Error incrementing AI usage:', error);
    return false;
  }
}

/**
 * Check if user has exceeded their AI usage limit
 */
export async function hasExceededLimit(): Promise<boolean> {
  const usage = await getAIUsage();
  if (!usage) return false;
  return usage.questions_used_this_month >= usage.limit;
}
